import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import * as types from '../constants/ActionTypes';
import { trackPage } from '../../lib/analytics-helper';
import { isTopicSupportedForStepUp, mappingRemoteConfig, isNodeChatAlreadyExists } from '../../lib/step-up-helper';
import { fetchFinalTopicContent } from './action-select-topic';
import { loadModalContainer, closeModalContainer } from './action-login-modal';
import { showChatBotWhenPresentOnPage, chatBotEligibilityResponse } from './action-contact-us';
import { stepUpChannels } from '../constants/UtilVariables';
import { getChatAction, isChatByPassEnable } from '../../lib/chat-bot-live-helper';
import * as AsyncHelper from '../../lib/asyncMessageHelper';
import Page from '../../lib/page';
import { loadChatIneligibleModalContainer } from './action-chat-ineligible-modal';
import { fetchChatEligibility } from './action-chat';
import { locationUpdate } from './action-contact-location';

const isChatBotPostServiceEnabled = Page.isChatBotPostServiceEnabled();
const isLexaChatEligibilityEnabled = Page.getIsIDIContactOptions();

const attempts = {
  FIRST: 'first',
  SECOND: 'second',
};
// --------------------------------- RISK [ Step Up]-----------------------------------------

export const loadStepUpContainer = () => ({ type: types.LOAD_STEP_UP_CONTAINER });

export const closeStepUpContainer = ({ redirectToChannel }) => ({
  type: types.CLOSE_STEP_UP_CONTAINER,
  payload: redirectToChannel,
});

export const resetStepUp = () => ({ type: types.RESET_STEP_UP });

export const requestRiskCheck = (fromChannel, selectedTopic) => ({ type: types.REQUEST_RISK_CHECK_INITIATED, fromChannel, selectedTopic });

export const receiveRiskError = () => ({ type: types.RECEIVE_RISK_ERROR });

export const receivedRiskAllow = () => ({ type: types.RECEIVED_RISK_ALLOW });

export const receiveRiskCheck = riskInfo => ({
  type: types.RECEIVE_RISK_CHECK,
  payload: riskInfo,
});

/**
 *  Get selected TopicRefId to perform risk check. TopicRefId is required to perform risk check.
 *  For already logged in user getting topicRefId from state.selectResult
 *  For logged Out to logged in case getting topicRefId from state.smcResultKeeper - which is from session storage.
 *
 * @param state
 * @returns topicRefId || undefined
 */
const getTopicRefId = (state = {}) => {
  const refId = (((state.selectResult || {}).topicTreeSelectResult || {}).topicRefId) ||
    (((state.smcResultKeeper || {}).topicTreeSelectResult || {}).topicRefId);
  return typeof refId === 'string' ? refId : undefined;
};

export const isLoggedOutToLogin = (state = {}) => !_isEmpty((state.modal || {}).fromChannel);


/**
 * Service call to perform RISK check.
 * 1) If RISK decision is STEPUP_AUTH open stepup container
 * 2) If RISK decision is STEPUP_AUTH and is from logged out to logged in then open final topic content and stepup container
 * 3) IF RISK decision is ALLOW redirect to the channel where step up is triggered from.
 * 4) Default redirect to the channel where step up is triggered from.
 *
 * @param topicRefId
 * @param fromChannel
 * @param attempt
 * @returns dispatches appropriate actions
 */
export const performStepUpCall = (topicRefId, fromChannel, attempt) => (dispatch, getState) => {
  const { persistedStepUp: { anw: { isRedirectedToAnw } } } = getState();
  const riskURL = `/smarthelp/stepup-risk?intentId=${topicRefId}&contactChannel=${fromChannel}&attempt=${attempt}&entryPoint=channelSteering`;

  return axios.get(riskURL)
    .then((response) => {
      const riskInfo = ((response || {}).data || {}).riskInfo;

      // Check risk decision and perform actions based on that.
      const riskDecision = riskInfo.decisions && riskInfo.decisions.length ? riskInfo.decisions[0].decision_code : '';

      // On Second attempt ignore the decision and fire an FPTI.
      if (attempt === attempts.SECOND) {
        return trackPage({ id: 'stepUpIntermediatePage',
          link: `smarthelp-step-up-intermediate|${topicRefId}|${fromChannel}|${attempt}|decision-${riskDecision}|anwRedirect-ignoreDecision` });
      }

      // If the attempt is first time perform the below operations.
      dispatch(receiveRiskCheck(riskInfo));

      // Fire FPTI with Decision
      trackPage({ id: 'stepUpIntermediatePage',
        link: `smarthelp-step-up-intermediate|${topicRefId}|${fromChannel}|${attempt}|decision-${riskDecision}` });

      switch (riskDecision) {
        case 'CONTINGENT': {
            // if the decision is STEP_UP then need to load final intent page for callUs channel before opening step-up modal window.
          if ((isLoggedOutToLogin(getState()) || isRedirectedToAnw) && fromChannel === stepUpChannels.call) {
            const { topic } = getState().persistedStepUp;
            dispatch(fetchFinalTopicContent({ topic }));
          }
            // Load step-up window
          return dispatch(loadStepUpContainer());
        }
        case 'ALLOW': {
          return dispatch(receivedRiskAllow());
        }
        default:
          return dispatch(receivedRiskAllow());
      }
    })
    .catch(() => {
      trackPage({ id: 'stepUpIntermediatePage',
        link: `smarthelp-step-up-intermediate|${topicRefId}|${fromChannel}|${attempt}|risk-failure` });

      // Do not dispatch error on second attempt.
      if (attempt !== attempts.SECOND) {
        dispatch(receiveRiskError());
      }
    });
};

/**
 * This is the start point for step up risk initiation.
 * Risk check will be performed only of the selected topic is supported for step-up
 * If not supported will be redirected / open the channel it was triggered from.
 *
 * @param fromChannel
 * @param selectedTopic
 * @param isOpenFinalIntentBeforeStepUp
 * @param attempt
 * @returns {Function}
 */
export const initiateRiskCheck = (fromChannel, selectedTopic, isOpenFinalIntentBeforeStepUp = false, attempt = attempts.FIRST) => (dispatch, getState) => {
  // Before initializing step-up check whether chat window is already open
  // When a chat window is already active do not perform step-up operation.
  // If fromChannel is chat and if node chat already exists return null
  if (mappingRemoteConfig[fromChannel] === 'chat' && isNodeChatAlreadyExists()) {
    return null;
  }

  const topicRefId = (selectedTopic && selectedTopic.topicRefId) || null;

  dispatch(requestRiskCheck(fromChannel, selectedTopic));

  // Only for logged out customers when click on ChatWithUs / callUs in contact options
  if (!Page.isLoggedIn()) {
    if (fromChannel === 'chat') {
      // ChatBotByPass for contact options
      sessionStorage.setItem('chatCardClicked', 'true'); // Removing chat card after user clicks on live chat since it failed to make users click on it.
      if (isChatByPassEnable(topicRefId)) {
        sessionStorage.setItem('chatBotClicked', true);
        return dispatch(loadModalContainer('chatBot'));
      }

      sessionStorage.setItem('liveChatClicked', true);
      return dispatch(loadModalContainer('liveChat'));
    }

    if (fromChannel === 'callUs') {
      return dispatch(loadModalContainer('callUs'));
    }
  }

  // If logged in and the selected topic is supported for step-up
  if (Page.isLoggedIn() && topicRefId) {
    // If selected topic is enabled for step-up
    if (isTopicSupportedForStepUp(topicRefId, fromChannel)) {
      const { topic, anw: { isRedirectedToAnw } } = getState().persistedStepUp;

      // If attempt is a second time which is a redirect from ANW.
      if (attempt === attempts.SECOND) {
        // If the fromChannel is from chatBot/liveChat/MessageUs open the final intent.
        if ((fromChannel === stepUpChannels.chatBot || fromChannel === stepUpChannels.liveChat ||
              fromChannel === stepUpChannels.chat || fromChannel === stepUpChannels.messageUs)
              && isOpenFinalIntentBeforeStepUp) {
          dispatch(fetchFinalTopicContent({ topic }));
        }

        // If there is service error during redirect to ANW UI then treat as allow to redirect the user to selected channel
        // and fire an FPTI with service error.
        if (Page.validateAnwIsRedirectError() && isRedirectedToAnw) {
          trackPage({ id: 'stepUpIntermediatePage',
            link: `smarthelp-step-up-intermediate|${topicRefId}|${fromChannel}|anw-redirect-service-error` });
          return dispatch(receivedRiskAllow());
        }

        // On Redirect from ANW whether the decision is ALLOW or STEPUP
        // we should allow the user to go the channel which was selected.
        dispatch(receivedRiskAllow());

        // Fire and forget the service call. Risk decision is ignored on redirected from ANW whether its success or error from ANW.
        return dispatch(performStepUpCall(topicRefId, fromChannel, attempt));

        // If the attempt if first time and If isOpenFinalIntentBeforeStepUp is set explicitly true
        // then will load the final intent before performing risk call.
        // Using this for chat, chatBot, MessageUs channels.
      } else if (isOpenFinalIntentBeforeStepUp) {
        dispatch(fetchFinalTopicContent({ topic }));
      }

      return dispatch(performStepUpCall(topicRefId, fromChannel, attempt));
    }
  }

  // Default case if topic is not supported or in any error cases skip step-up and redirect to selected channel.
  return dispatch(receivedRiskAllow());
};


// --------------------------- ANW [Auth Node Web]-----------------------------------

export const requestAnwCheck = () => ({ type: types.REQUEST_ANW_CHECK_INITIATED });

export const receivedANWCheck = anwInfo => ({
  type: types.RECEIVE_ANW_SUCCESS,
  payload: anwInfo,
});

export const receivedANWError = () => ({ type: types.RECEIVE_ANW_ERROR });


const getContextId = (state = {}) => {
  const authContextId = ((state.stepUp || {}).riskInfo || {}).authflow_context_id;
  return typeof authContextId === 'string' ? authContextId : '';
};


export const initiateANWCheck = () => (dispatch, getState) => {
  dispatch(requestAnwCheck());
  const state = getState();
  const contextId = getContextId(state);
  const { stepUp: { fromChannel } } = state;
  const anwURL = `/smarthelp/stepup-anw?contextId=${contextId}`;

  return axios.get(anwURL)
    .then((response) => {
      const anwInfo = ((response || {}).data || {}).authNodeWebUrl;
      dispatch(receivedANWCheck(anwInfo));
    })
    .catch(() => {
      trackPage({ id: 'stepUpIntermediatePage',
        link: `smarthelp-step-up-intermediate|${(getTopicRefId(state))}|${fromChannel}|anw-failure` });
      dispatch(receivedANWError());
    });
};

// ------------------------  Common place for redirect logic ------------------------------

// The complete redirect logic for step-up based on the user selections.
// TODO:: This logic will be refactored after chat/ChatBot refactoring.
export const redirectToAppropriateChannel = () => (dispatch, getState) => {
  const state = getState();
  const { persistedStepUp: { topic, anw: { isRedirectedToAnw } }, stepUp: { fromChannel } } = state;

  switch (fromChannel) {
    case stepUpChannels.call: {
      dispatch(locationUpdate({ pageName: fromChannel }));
      if (isRedirectedToAnw && isChatBotPostServiceEnabled) {
        dispatch(chatBotEligibilityResponse()); // Combine with Pilot user checker
      }
      break;
    }

    case stepUpChannels.chat: {
      getChatAction(getTopicRefId(state));
      if (isRedirectedToAnw && isChatBotPostServiceEnabled) {
        dispatch(chatBotEligibilityResponse()); // Combine with Pilot user checker
      }
      break;
    }

    case stepUpChannels.messageUs: {
      AsyncHelper.initiateAsyncChat(getTopicRefId(state));
      if (isRedirectedToAnw && isChatBotPostServiceEnabled) {
        dispatch(chatBotEligibilityResponse()); // Combine with Pilot user checker
      }
      break;
    }

    case stepUpChannels.chatBot: {
      if (isLoggedOutToLogin(getState()) || isRedirectedToAnw) {
        if (isChatBotPostServiceEnabled) {
          dispatch(chatBotEligibilityResponse()); // Combine with Pilot user checker
        } else {
          dispatch(showChatBotWhenPresentOnPage(topic, fromChannel));
        }
      }
      break;
    }

    case stepUpChannels.liveChat: {
      if (isChatBotPostServiceEnabled) {
        dispatch(chatBotEligibilityResponse());
      }

      const options = {
        topic,
        fromChannel,
        showChat: showChatBotWhenPresentOnPage, // Combine with Pilot user checker
        reject: loadChatIneligibleModalContainer,
      };

      // If user is logged out to logged in / redirected from anw and Topic is supported for step-up then fetch chat eligibilty.
      // Here we dont have to fetch final topic content as final topic content is already loaded.
      if (!isLexaChatEligibilityEnabled && (isLoggedOutToLogin(getState()) || isRedirectedToAnw) && !_isEmpty(topic) && isTopicSupportedForStepUp(topic.topicRefId, fromChannel)) {
        dispatch(fetchChatEligibility(topic.topicRefId, options.fromChannel, options.showChat, options.reject));

        // If topic is not supported for step-up fetch final topic content.
      } else if (!_isEmpty(topic)) {
        dispatch(fetchFinalTopicContent(options));
      } else { // Live Chat in the default contact options
        const defaultChatDecision = Page.getDefaultChatDecision(); // Get Live Chat availability decision in default contact options
        if (defaultChatDecision && defaultChatDecision.availability_decision === 'AVAILABLE') {
          dispatch(showChatBotWhenPresentOnPage({ topicRefId: 'GENPOOL' }, fromChannel)); // Invoke Live Chat
        } else {
          dispatch(loadChatIneligibleModalContainer());
        }
      }
      break;
    }

    default:
      break;
  }

  dispatch(resetStepUp());
  dispatch(closeModalContainer());
};
